// 🚀 FRONTEND BADGES MANAGEMENT - Routes API Corrigées
import { API_BASE } from '../config';
import React, { useState, useEffect } from 'react';
import { QrCode, Badge, User, RefreshCw, Loader2, Download, Clock, Lock, AlertCircle, CheckCircle, XCircle, X, Home, PanelRightOpen } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
import LanguageSelector from './LanguageSelector';

const MOBILE_BREAKPOINT = 768;

const BadgeManagement = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [badges, setBadges] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatingEmployeeId, setGeneratingEmployeeId] = useState(null); // Per-employee loading to avoid React DOM reconciliation errors
  const [qrImageErrors, setQrImageErrors] = useState({}); // Track QR image load failures (avoid direct DOM manipulation)
  const [view, setView] = useState('list'); // 'list', 'create', 'quick-check'
  const [message, setMessage] = useState('');
  const [quickCheckCode, setQuickCheckCode] = useState('');
  const [quickCheckPassword, setQuickCheckPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const update = () => {
      setIsMobile(mql.matches);
      if (!mql.matches) setDrawerOpen(false);
    };
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  // ==================== FONCTIONS UTILITAIRES ROBUSTES ====================
  
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Token d\'authentification manquant. Reconnectez-vous pour accéder aux badges.');
    }
    
    console.log('🎫 BADGES: Token récupéré pour headers:', token.substring(0, 30) + '...');
    
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Cache-Control': 'no-cache'
    };
  };

  const showMessage = (msg, type = 'info') => {
    console.log(`📢 BADGES MESSAGE [${type}]:`, msg);
    setMessage(msg);
    setTimeout(() => setMessage(''), 8000); // Message plus long pour debug
  };

  // Fonction utilitaire pour gérer les erreurs d'authentification
  const handleAuthError = async (response, errorText) => {
    if (response.status === 401 || response.status === 403) {
      try {
        const errorData = JSON.parse(errorText);
        if (errorData.error && (errorData.error.includes('expired') || errorData.error.includes('invalid'))) {
          showMessage('❌ ' + t('badges.session_expired'), 'error');
          setTimeout(() => {
            localStorage.removeItem('token');
            window.location.href = '/login';
          }, 2000);
          return true; // Indique qu'une redirection est en cours
        }
      } catch (e) {
        // Si le parsing échoue, continuer avec le traitement normal
      }
      
      showMessage('❌ ' + t('badges.token_invalid'), 'error');
      setTimeout(() => {
        if (window.confirm(t('badges.session_expired_confirm') || 'Session expirée. Voulez-vous vous reconnecter ?')) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
      }, 2000);
      return true; // Indique qu'une redirection est en cours
    }
    return false; // Pas d'erreur d'authentification
  };

  // ==================== FONCTIONS API AVEC DEBUG AVANCÉ ====================

  // Test de connectivité API Badges avec diagnostic JWT
  const testBadgesAPI = async () => {
    try {
      console.log('🧪 BADGES: Test connectivité API avec diagnostic JWT...');
      
      const token = localStorage.getItem('token');
      console.log('🎫 BADGES: Token stocké (preview):', token ? token.substring(0, 20) + '...' : 'MANQUANT');
      
      if (!token) {
        console.error('❌ BADGES: Aucun token trouvé dans localStorage');
        showMessage('❌ ' + t('badges.session_expired'), 'error');
        return false;
      }

      // Test avec route de santé d'abord
      const headers = getAuthHeaders();
      console.log('📡 BADGES: Headers préparés pour test:', Object.keys(headers));
      
      const response = await fetch(`${API_BASE}/api/badges/health`, { 
        method: 'GET',
        headers 
      });
      
      console.log('🧪 BADGES: Réponse test API:', {
        status: response.status,
        statusText: response.statusText,
        url: response.url
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ BADGES: API badges fonctionnelle:', data);
        return true;
      } else if (response.status === 401 || response.status === 403) {
        const errorData = await response.text();
        console.error('❌ BADGES: Erreur authentification:', {
          status: response.status,
          error: errorData
        });
        showMessage('❌ ' + t('badges.token_invalid'), 'error');
        
        // Optionnel : rediriger vers login
        setTimeout(() => {
          if (window.confirm(t('badges.session_expired_confirm') || 'Session expirée. Voulez-vous vous reconnecter ?')) {
            window.location.href = '/login';
          }
        }, 2000);
        
        return false;
      } else {
        console.error('❌ BADGES: API non fonctionnelle:', response.status);
        await response.text();
        showMessage(`❌ ${t('badges.api_unavailable')} (${response.status})`, 'error');
        return false;
      }
    } catch (error) {
      console.error('💥 BADGES: Erreur test API:', error);
      showMessage('❌ ' + t('badges.api_unavailable'), 'error');
      return false;
    }
  };

  // Récupérer les badges existants avec debug
  const fetchBadges = async () => {
    try {
      setLoading(true);
      console.log('🔄 BADGES: Récupération badges...');
      
      // Test préliminaire de l'API
      const apiWorking = await testBadgesAPI();
      if (!apiWorking) {
        throw new Error('API Badges non accessible');
      }
      
      const headers = getAuthHeaders();
      console.log('📡 BADGES: Headers préparés:', Object.keys(headers));
      
      const response = await fetch(`${API_BASE}/api/badges/all`, { 
        method: 'GET',
        headers 
      });
      
      console.log('📡 BADGES: Réponse badges:', {
        status: response.status,
        statusText: response.statusText,
        url: response.url
      });
      
      if (!response.ok) {
        const errBody = await response.text();
        console.error('❌ BADGES: Erreur détaillée:', {
          status: response.status,
          body: errBody
        });
        
        // Gestion des erreurs d'authentification
        const isAuthError = await handleAuthError(response, errBody);
        if (isAuthError) {
          return; // Redirection en cours, arrêter l'exécution
        }
        
        throw new Error(`HTTP ${response.status}: ${errBody || 'Erreur API badges'}`);
      }
      
      const data = await response.json();
      console.log('📋 BADGES: Données reçues:', {
        success: data.success,
        badgesCount: data.badges?.length || 0,
        total: data.total,
        message: data.message
      });
      
      if (data.success) {
        setBadges(data.badges || []);
        setQrImageErrors({}); // Reset image error state on refresh
        console.log('✅ BADGES: Badges chargés:', data.badges?.length || 0);
        
        if (data.badges?.length === 0) {
          showMessage('ℹ️ ' + t('badges.no_badges_found'), 'info');
        } else {
          showMessage('✅ ' + t('badges.success_loaded_count', { count: data.badges.length }), 'success');
        }
      } else {
        console.error('❌ BADGES: Erreur API badges:', data.error);
        showMessage('❌ ' + (data.error || t('badges.error_connection')), 'error');
        setBadges([]);
      }
    } catch (error) {
      console.error('💥 BADGES: Erreur complète récupération badges:', {
        message: error.message,
        stack: error.stack
      });
      showMessage('❌ ' + t('badges.error_connection') + ': ' + error.message, 'error');
      setBadges([]);
    } finally {
      setLoading(false);
    }
  };

  // Récupérer les employés avec debug
  const fetchEmployees = async () => {
    try {
      console.log('🔄 BADGES: Récupération employés...');
      
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/employees?all=true&active=all`, { 
        method: 'GET',
        headers 
      });
      
      console.log('📡 BADGES: Réponse employés:', {
        status: response.status,
        url: response.url
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📋 BADGES: DONNÉES EMPLOYÉS REÇUES:', {
        success: data.success,
        employeesCount: (data.employees || data.data || [])?.length || 0
      });
      
      if (data.success) {
        // ✅ CORRECTION : Gérer la structure paginée (data.employees ou data.data)
        const employeesList = data.employees || data.data || [];
        if (Array.isArray(employeesList)) {
          const mappedEmployees = employeesList.map(emp => ({
          id: emp.id,
          first_name: emp.firstName || emp.first_name || emp.firstname || 'Prénom',
          last_name: emp.lastName || emp.last_name || emp.lastname || 'Nom',
          employee_code: emp.employeeCode || emp.employee_code || emp.code || 'CODE',
          department: emp.department || 'Non défini',
          position: emp.position || 'Non défini',
          email: emp.email || '',
          user_id: emp.user_id || emp.userId || null,
          photo_url: emp.photo_url || emp.photoUrl || null
        }));
        
          console.log('👥 BADGES: EMPLOYÉS MAPPÉS:', mappedEmployees.length);
          setEmployees(mappedEmployees);
          
          if (mappedEmployees.length === 0) {
            showMessage('ℹ️ ' + t('badges.no_employees_found'), 'info');
          }
        } else {
          console.error('❌ BADGES: STRUCTURE EMPLOYÉS INATTENDUE (pas un tableau):', employeesList);
          setEmployees([]);
          showMessage('❌ ' + (t('badges.error_structure') || 'Erreur structure données employés'), 'error');
        }
      } else {
        console.error('❌ BADGES: RÉPONSE API ÉCHOUÉE:', data);
        setEmployees([]);
        showMessage('❌ ' + (t('badges.error_structure') || 'Erreur structure données employés'), 'error');
      }
    } catch (error) {
      console.error('💥 BADGES: ERREUR RÉCUPÉRATION EMPLOYÉS:', error);
      setEmployees([]);
      showMessage('❌ ' + (t('badges.error_employees') || 'Erreur de connexion aux employés') + ': ' + error.message, 'error');
    }
  };

  // ==================== ACTIONS BADGES AVEC DEBUG AVANCÉ ====================

  // Générer un badge pour un employé
  const generateBadge = async (employeeId) => {
    try {
      setGeneratingEmployeeId(employeeId);
      setLoading(true);
      console.log('🔐 BADGES: Génération badge pour employé:', employeeId);
      
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/badges/employee/${employeeId}`, {
        method: 'GET',
        headers
      });
      
      console.log('📡 BADGES: Réponse génération badge:', {
        status: response.status,
        url: response.url
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ BADGES: Erreur génération:', errorText);
        const isAuthError = await handleAuthError(response, errorText);
        if (isAuthError) {
          return; // Redirection en cours
        }
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || `HTTP ${response.status}`);
        } catch (e) {
          throw new Error(`HTTP ${response.status}: ${errorText || 'Erreur génération'}`);
        }
      }
      
      const data = await response.json();
      console.log('📋 BADGES: Données badge générées:', {
        success: data.success,
        badgeCode: data.badge?.badge_code,
        message: data.message
      });
      
      if (data.success) {
        showMessage('✅ ' + data.message, 'success');
        await fetchBadges(); // Recharger la liste
        return data.badge;
      } else {
        throw new Error(data.error || 'Erreur génération inconnue');
      }
    } catch (error) {
      console.error('💥 BADGES: Erreur génération badge:', error);
      showMessage('❌ Erreur génération: ' + error.message, 'error');
    } finally {
      setGeneratingEmployeeId(null);
      setLoading(false);
    }
  };

  // Générer tous les badges pour employés sans badge
  const generateAllBadges = async () => {
    if (employeesWithoutBadges.length === 0) return;
    setGeneratingEmployeeId('all');
    setLoading(true);
    for (const emp of employeesWithoutBadges) {
      try {
        const headers = getAuthHeaders();
        const response = await fetch(`${API_BASE}/api/badges/employee/${emp.id}`, { method: 'GET', headers });
        if (response.ok) {
          const data = await response.json();
          if (data.success) showMessage(`✅ ${emp.first_name} ${emp.last_name}: ${data.message}`, 'success');
        }
      } catch (e) {
        console.error('Erreur badge', emp.id, e);
      }
    }
    setGeneratingEmployeeId(null);
    setLoading(false);
    await fetchBadges();
    await fetchEmployees();
  };

  // Régénérer un badge avec confirmation
  const regenerateBadge = async (employeeId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir régénérer ce badge ? L\'ancien badge sera désactivé.')) {
      return;
    }

    try {
      setLoading(true);
      console.log('🔄 BADGES: Régénération badge pour employé:', employeeId);
      
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/badges/regenerate/${employeeId}`, {
        method: 'POST',
        headers
      });
      
      console.log('📡 BADGES: Réponse régénération badge:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        const isAuthError = await handleAuthError(response, errorText);
        if (isAuthError) {
          return; // Redirection en cours
        }
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || `HTTP ${response.status}`);
        } catch (e) {
          throw new Error(`HTTP ${response.status}: ${errorText || 'Erreur régénération'}`);
        }
      }
      
      const data = await response.json();
      console.log('📋 BADGES: Données régénération:', {
        success: data.success,
        message: data.message
      });
      
      if (data.success) {
        showMessage('✅ ' + data.message, 'success');
        await fetchBadges(); // Recharger la liste
      } else {
        throw new Error(data.error || 'Erreur régénération inconnue');
      }
    } catch (error) {
      console.error('💥 BADGES: Erreur régénération badge:', error);
      showMessage('❌ Erreur régénération: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Pointage rapide par code avec mot de passe et debug
  const quickAttendance = async () => {
    if (!quickCheckCode.trim()) {
      showMessage('❌ Veuillez saisir un code employé', 'error');
      return;
    }

    if (!quickCheckPassword.trim()) {
      showMessage('❌ Veuillez saisir le mot de passe', 'error');
      return;
    }

    try {
      setLoading(true);
      console.log('⚡ BADGES: Pointage rapide pour:', quickCheckCode);
      
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/badges/attendance`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          employeeCode: quickCheckCode.trim().toUpperCase(),
          password: quickCheckPassword.trim()
        })
      });
      
      console.log('📡 BADGES: Réponse pointage:', {
        status: response.status,
        url: response.url
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ BADGES: Erreur pointage:', errorText);
        const isAuthError = await handleAuthError(response, errorText);
        if (isAuthError) {
          return; // Redirection en cours
        }
        let errorMsg = t('badges.error_connection') || 'Erreur pointage';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.error) errorMsg = errorData.error;
        } catch (_e) { /* garder message générique si pas JSON */ }
        throw new Error(errorMsg);
      }
      
      const data = await response.json();
      console.log('📋 BADGES: Données pointage:', {
        success: data.success,
        type: data.attendance?.type,
        employee: data.attendance?.employee?.first_name + ' ' + data.attendance?.employee?.last_name
      });
      
      if (data.success) {
        const employee = data.attendance.employee;
        showMessage(`✅ ${data.message} - ${employee.first_name} ${employee.last_name}`, 'success');
        setQuickCheckCode('');
        setQuickCheckPassword('');
      } else {
        throw new Error(data.error || 'Erreur pointage');
      }
    } catch (error) {
      console.error('💥 BADGES: Erreur pointage:', error);
      // Extraire uniquement le message d'erreur (pas l'objet JSON complet)
      let displayError = error.message;
      try {
        const jsonMatch = error.message?.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          if (parsed.error) displayError = parsed.error;
        }
      } catch (_e) { /* garder error.message */ }
      showMessage('❌ ' + displayError, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Télécharger le badge en PNG avec design amélioré et photo de profil
  const downloadBadgePNG = async (badge) => {
    try {
      console.log('📥 BADGES: Début téléchargement badge:', badge.badge_code);
      
      // Récupérer la photo de profil de l'employé si disponible
      let profilePhotoUrl = null;
      if (badge.photo_url) {
        profilePhotoUrl = `${API_BASE}${badge.photo_url}`;
        console.log('📸 BADGES: Photo de profil trouvée:', profilePhotoUrl);
      }
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const qrImg = new Image();
      const profileImg = profilePhotoUrl ? new Image() : null;
      
      // Fonction pour dessiner le badge une fois toutes les images chargées
      const drawBadge = () => {
        console.log('🖼️ BADGES: Images chargées, génération PNG...');
        
        // Dimensions améliorées pour un badge professionnel - augmentées pour éviter les coupures
        canvas.width = 700;
        canvas.height = 500; // Augmenté de 450 à 500 pour plus d'espace
        
        // Fond dégradé élégant
        const gradient = ctx.createLinearGradient(0, 0, 700, 500);
        gradient.addColorStop(0, '#ffffff');
        gradient.addColorStop(1, '#f8f9ff');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, 700, 500);
        
        // Bordure moderne avec ombre
        ctx.shadowColor = 'rgba(102, 126, 234, 0.3)';
        ctx.shadowBlur = 15;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 5;
        ctx.strokeStyle = '#667eea';
        ctx.lineWidth = 4;
        ctx.strokeRect(15, 15, 670, 470); // Ajusté pour la nouvelle hauteur
        ctx.shadowBlur = 0;
        
        // En-tête avec fond coloré
        const headerGradient = ctx.createLinearGradient(0, 0, 700, 0);
        headerGradient.addColorStop(0, '#667eea');
        headerGradient.addColorStop(1, '#764ba2');
        ctx.fillStyle = headerGradient;
        ctx.fillRect(15, 15, 670, 80);
        
        // Nom de l'entreprise (si disponible)
        if (companyName) {
          ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
          ctx.font = 'bold 20px "Segoe UI", Arial, sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(companyName.toUpperCase(), 350, 35);
        }
        
        // Titre avec style moderne
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 32px "Segoe UI", Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('BADGE EMPLOYÉ', 350, 60);
        
        // Ligne décorative sous le titre
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(60, 75);
        ctx.lineTo(640, 75);
        ctx.stroke();
        
        // Photo de profil ou initiales
        const photoSize = 70; // Légèrement réduit pour plus d'espace
        const photoX = 350 - photoSize / 2;
        const photoY = 110;
        
        if (profileImg && profileImg.complete && profileImg.naturalWidth > 0) {
          // Dessiner la photo de profil avec bordure circulaire
          ctx.save();
          ctx.beginPath();
          ctx.arc(350, photoY + photoSize / 2, photoSize / 2, 0, Math.PI * 2);
          ctx.clip();
          ctx.drawImage(profileImg, photoX, photoY, photoSize, photoSize);
          ctx.restore();
          
          // Bordure autour de la photo
          ctx.strokeStyle = '#667eea';
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.arc(350, photoY + photoSize / 2, photoSize / 2, 0, Math.PI * 2);
          ctx.stroke();
        } else {
          // Dessiner les initiales dans un cercle
          const initials = `${badge.first_name?.[0] || ''}${badge.last_name?.[0] || ''}`.toUpperCase();
          const initialsGradient = ctx.createLinearGradient(photoX, photoY, photoX + photoSize, photoY + photoSize);
          initialsGradient.addColorStop(0, '#14b8a6');
          initialsGradient.addColorStop(1, '#06b6d4');
          ctx.fillStyle = initialsGradient;
          ctx.beginPath();
          ctx.arc(350, photoY + photoSize / 2, photoSize / 2, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = '#667eea';
          ctx.lineWidth = 3;
          ctx.stroke();
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 28px "Segoe UI", Arial, sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(initials, 350, photoY + photoSize / 2);
        }
        
        // Informations employé avec meilleure mise en page
        const infoY = photoY + photoSize + 18; // Espacement ajusté
        ctx.fillStyle = '#1f2937';
        ctx.font = 'bold 26px "Segoe UI", Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';
        
        // Mesurer la largeur du nom pour éviter les débordements
        const fullName = `${badge.first_name} ${badge.last_name}`;
        const nameMetrics = ctx.measureText(fullName);
        if (nameMetrics.width > 650) {
          // Si le nom est trop long, réduire la taille de police
          ctx.font = 'bold 22px "Segoe UI", Arial, sans-serif';
        }
        ctx.fillText(fullName, 350, infoY);
        
        // Code employé avec icône visuelle - espacement augmenté
        const codeY = infoY + 32; // Espacement augmenté pour éviter les coupures
        ctx.fillStyle = '#667eea';
        ctx.font = 'bold 18px "Segoe UI", Arial, sans-serif';
        ctx.textBaseline = 'alphabetic';
        
        // Mesurer et ajuster si nécessaire
        const codeText = `Code: ${badge.employee_code}`;
        const codeMetrics = ctx.measureText(codeText);
        if (codeMetrics.width > 650) {
          ctx.font = 'bold 16px "Segoe UI", Arial, sans-serif';
        }
        ctx.fillText(codeText, 350, codeY);
        
        // QR Code centré avec cadre moderne - position ajustée avec plus d'espace
        const qrSize = 180; // Légèrement réduit pour éviter les coupures
        const qrX = (700 - qrSize) / 2;
        const qrY = codeY + 50; // Plus d'espace après le code employé
        
        // Fond QR avec ombre
        ctx.shadowColor = 'rgba(102, 126, 234, 0.2)';
        ctx.shadowBlur = 10;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 3;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(qrX - 15, qrY - 15, qrSize + 30, qrSize + 30);
        ctx.shadowBlur = 0;
        
        // Bordure QR avec style moderne
        ctx.strokeStyle = '#667eea';
        ctx.lineWidth = 3;
        ctx.strokeRect(qrX - 15, qrY - 15, qrSize + 30, qrSize + 30);
        
        // Double bordure intérieure
        ctx.strokeStyle = '#e0e7ff';
        ctx.lineWidth = 2;
        ctx.strokeRect(qrX - 10, qrY - 10, qrSize + 20, qrSize + 20);
        
        // Dessiner le QR code
        ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
        
        // Informations en bas avec style professionnel - position ajustée
        const footerY = qrY + qrSize + 35; // Position dynamique basée sur le QR code
        ctx.fillStyle = '#9ca3af';
        ctx.font = '12px "Segoe UI", Arial, sans-serif';
        ctx.textAlign = 'center';
        
        // Date création
        const createdDate = new Date(badge.created_at).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
        ctx.fillText(`Créé le: ${createdDate}`, 350, footerY);
        
        // Code badge avec style discret
        ctx.fillStyle = '#6b7280';
        ctx.font = '11px "Segoe UI", Arial, sans-serif';
        ctx.fillText(`Badge: ${badge.badge_code}`, 350, footerY + 18);
        
        // Télécharger
        const link = document.createElement('a');
        link.download = `badge_${badge.employee_code}_${badge.first_name}_${badge.last_name}.png`;
        link.href = canvas.toDataURL('image/png', 1.0);
        link.click();
        
        console.log('✅ BADGES: Badge PNG téléchargé avec succès');
        showMessage('✅ ' + t('badges.success_downloaded'), 'success');
      };
      
      // Charger le QR code
      qrImg.onload = () => {
        if (profileImg) {
          profileImg.onload = drawBadge;
          profileImg.onerror = () => {
            console.log('ℹ️ BADGES: Photo de profil non disponible, utilisation des initiales');
            drawBadge();
          };
          profileImg.crossOrigin = 'anonymous';
          profileImg.src = profilePhotoUrl;
        } else {
          drawBadge();
        }
      };
      
      qrImg.onerror = (error) => {
        console.error('❌ BADGES: Erreur chargement image QR:', error);
        showMessage('❌ Erreur lors du téléchargement du badge', 'error');
      };
      
      qrImg.src = badge.qr_code_image;
    } catch (error) {
      console.error('❌ BADGES: Erreur téléchargement:', error);
      showMessage('❌ Erreur lors du téléchargement: ' + error.message, 'error');
    }
  };

  // ==================== EFFECTS ====================

  // Récupérer le nom de l'entreprise
  const fetchCompanyName = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE}/api/auth/me`, {
        method: 'GET',
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.user && (data.user.company || data.user.companyName)) {
          setCompanyName(data.user.company || data.user.companyName);
          console.log('✅ BADGES: Nom entreprise récupéré:', data.user.company || data.user.companyName);
        } else if (data.company) {
          setCompanyName(data.company);
          console.log('✅ BADGES: Nom entreprise récupéré:', data.company);
        }
      }
    } catch (error) {
      console.error('❌ BADGES: Erreur récupération nom entreprise:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('🚀 BADGES: Chargement initial des données...');
        
        // Charger en parallèle
        await Promise.all([fetchBadges(), fetchEmployees(), fetchCompanyName()]);
        
        console.log('✅ BADGES: Chargement initial terminé');
      } catch (error) {
        console.error('❌ BADGES: Erreur chargement initial:', error);
        showMessage('❌ Erreur de chargement initial: ' + error.message, 'error');
      }
    };
    
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==================== DERIVED DATA ====================

  const employeesWithoutBadges = employees.filter(emp => 
    !badges.some(badge => badge.employee_id === emp.id)
  );

  // ==================== RENDER AVEC AMÉLIORATION UI ====================

  return (
    <div style={{ 
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: isMobile ? '16px 16px 12px' : '24px 20px 20px',
        background: 'transparent',
        backdropFilter: 'none',
        borderBottom: 'none',
        zIndex: 999,
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          maxWidth: '100%',
          minWidth: 0
        }}>
          {isMobile ? (
            <>
              <div style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                overflow: 'hidden'
              }}>
                <QrCode size={28} style={{ flexShrink: 0 }} />
                <span style={{
                  fontSize: '22px',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>{t('badges.title') || 'Badges & QR'}</span>
              </div>
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                aria-label={t('home.open_menu') || 'Menu'}
                title={t('home.open_menu') || 'Menu'}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              >
                <PanelRightOpen size={20} />
              </button>
            </>
          ) : (
            <>
              {onNavigate && (
                <button
                  onClick={() => onNavigate('home')}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: '#ffffff',
                    padding: '10px',
                    borderRadius: '12px',
                    fontSize: '18px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                    backdropFilter: 'blur(10px)',
                    flexShrink: 0,
                    width: '44px',
                    height: '44px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title={t('common.back_to_home')}
                >
                  <Home size={20} />
                </button>
              )}
              <div style={{
                fontSize: 'clamp(28px, 5vw, 40px)',
                fontWeight: 700,
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                minWidth: 0
              }}>
                <QrCode size={32} />
                {t('badges.title') || 'Badges & QR Codes'}
              </div>
            </>
          )}
        </div>
        {!isMobile && (
          <p style={{
            fontSize: 'clamp(14px, 2vw, 16px)',
            color: 'rgba(255, 255, 255, 0.7)',
            margin: '8px 0 0 0',
            letterSpacing: '0.2px',
            fontWeight: 500
          }}>
            {t('badges.subtitle') || 'Générez et contrôlez les accès sécurisés de vos équipes'}
          </p>
        )}
      </div>

      {/* Mobile Drawer */}
      {isMobile && drawerOpen && (
        <>
          <div
            onClick={() => setDrawerOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 1300
            }}
          />
          <div style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: 'min(320px, 88vw)',
            background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
            borderLeft: '1px solid rgba(255,255,255,0.12)',
            zIndex: 1301,
            padding: '20px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>{t('common.menu') || 'Menu'}</span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label={t('common.close') || 'Close'}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#fff',
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <X size={20} />
              </button>
            </div>
            {onNavigate && (
              <button
                onClick={() => { onNavigate('home'); setDrawerOpen(false); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 12,
                  color: '#fff',
                  fontSize: 15,
                  fontWeight: 500,
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                <Home size={20} />
                {t('common.back_to_home')}
              </button>
            )}
            <div style={{ marginTop: 'auto', paddingTop: 16 }}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>{t('common.language') || 'Language'}</div>
              <LanguageSelector style={{ width: '100%', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', padding: '6px 10px', borderRadius: 10 }} />
            </div>
          </div>
        </>
      )}

      <div className="badges-content" style={{
        paddingTop: isMobile ? '88px' : '140px',
        paddingBottom: '40px',
        paddingLeft: '20px',
        paddingRight: '20px',
        maxWidth: '1400px',
        margin: '0 auto'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          background: 'rgba(255,255,255,0.05)', 
          backdropFilter: 'blur(20px)',
          borderRadius: '20px', 
          padding: isMobile ? '16px' : '20px', 
          marginBottom: '20px',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 10px 30px rgba(0,0,0,0.25)'
        }}>
          <h1 style={{ 
            color: '#ffffff', 
            margin: '0 0 20px 0', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px',
            fontSize: isMobile ? 'clamp(16px, 4vw, 20px)' : undefined
          }}>
            <QrCode size={isMobile ? 24 : 32} />
            🔖 {t('badges.management') || 'Gestion des Badges & QR Codes'}
          </h1>
          
          {/* Navigation avec statuts */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {[
              { key: 'list', label: `📋 ${t('badges.existing_badges') || 'Badges Existants'}`, count: badges.length, status: badges.length > 0 ? 'success' : 'info' },
              { key: 'create', label: `➕ ${t('badges.create_badge') || 'Créer Badge'}`, count: employeesWithoutBadges.length, status: employeesWithoutBadges.length > 0 ? 'warning' : 'success' },
              { key: 'quick-check', label: `⚡ ${t('badges.quick_check') || 'Pointage Rapide'}`, count: null, status: 'info' }
            ].map(viewType => (
              <button
                key={viewType.key}
                onClick={() => setView(viewType.key)}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '25px',
                  background: view === viewType.key ? 'white' : 'rgba(255,255,255,0.2)',
                  color: view === viewType.key ? '#667eea' : 'white',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {viewType.label}
                {viewType.count !== null && (
                  <span style={{
                    background: view === viewType.key ? 
                      (viewType.status === 'success' ? '#10b981' : viewType.status === 'warning' ? '#f59e0b' : '#667eea') : 
                      'rgba(255,255,255,0.3)',
                    color: 'white',
                    borderRadius: '12px',
                    padding: '2px 8px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    {viewType.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Messages avec icônes */}
        {message && (
          <div style={{
            background: message.includes('✅') ? 'rgba(16, 185, 129, 0.15)' : message.includes('ℹ️') ? 'rgba(59, 130, 246, 0.15)' : 'rgba(239, 68, 68, 0.15)',
            color: message.includes('✅') ? '#34d399' : message.includes('ℹ️') ? '#93c5fd' : '#fca5a5',
            padding: '15px',
            borderRadius: '12px',
            marginBottom: '20px',
            border: `1px solid ${message.includes('✅') ? 'rgba(16, 185, 129, 0.4)' : message.includes('ℹ️') ? 'rgba(59, 130, 246, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`,
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            animation: 'fadeIn 0.3s ease'
          }}>
            <span style={{ display: 'inline-flex', flexShrink: 0 }}>
              {message.includes('✅') ? <CheckCircle size={20} /> : 
               message.includes('ℹ️') ? <AlertCircle size={20} /> : 
               <XCircle size={20} />}
            </span>
            {message.replace(/^[✅ℹ️❌]\s*/, '')}
          </div>
        )}

        {/* Liste des badges existants */}
        {view === 'list' && (
          <div style={{ 
            background: 'rgba(255,255,255,0.05)', 
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '20px', 
            padding: '24px',
            boxShadow: '0 20px 45px rgba(0,0,0,0.35)'
          }}>
            <div style={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between', 
              alignItems: isMobile ? 'flex-start' : 'center',
              gap: isMobile ? '12px' : 0,
              marginBottom: '20px' 
            }}>
              <h2 style={{ margin: 0, color: 'rgba(255, 255, 255, 0.9)', fontSize: isMobile ? '1.1rem' : undefined }}>
                📋 {t('badges.active_badges') || 'Badges Actifs'} ({badges.length})
              </h2>
              <button
                onClick={fetchBadges}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '8px',
                  background: loading ? '#6b7280' : '#667eea',
                  color: 'white',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background-color 0.3s ease'
                }}
              >
                <span style={{ display: 'inline-flex' }}>
                  <RefreshCw size={16} style={{ 
                    animation: loading ? 'spin 1s linear infinite' : 'none' 
                  }} />
                </span>
                {t('badges.refresh')}
              </button>
            </div>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
              gap: '20px' 
            }}>
              {badges.map(badge => (
                <div key={badge.employee_id} style={{
                  background: 'rgba(15, 23, 42, 0.65)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '18px',
                  padding: '20px',
                  boxShadow: '0 20px 35px rgba(0,0,0,0.4)',
                  transition: 'transform 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '15px', 
                    marginBottom: '15px' 
                  }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '18px'
                    }}>
                      {badge.first_name?.charAt(0) || 'P'}{badge.last_name?.charAt(0) || 'N'}
                    </div>
                    <div>
                      <h3 style={{ margin: '0 0 5px 0', color: 'rgba(255, 255, 255, 0.9)' }}>
                        {badge.first_name} {badge.last_name}
                      </h3>
                      <p style={{ margin: 0, color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
                        {badge.employee_code} • {badge.department}
                      </p>
                    </div>
                  </div>

                  <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                    {!qrImageErrors[badge.employee_id] ? (
                      <img 
                        src={badge.qr_code_image} 
                        alt="QR Code Badge" 
                        style={{ 
                          width: '120px', 
                          height: '120px', 
                          border: '3px solid white',
                          borderRadius: '10px',
                          boxShadow: '0 3px 10px rgba(0,0,0,0.1)'
                        }} 
                        onError={() => setQrImageErrors(prev => ({ ...prev, [badge.employee_id]: true }))}
                      />
                    ) : (
                      <div style={{ 
                        width: '120px', 
                        height: '120px', 
                        border: '3px solid white',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(255,255,255,0.08)',
                        margin: '0 auto',
                        fontSize: '12px',
                        color: '#666',
                        flexDirection: 'column',
                        gap: '5px'
                      }}>
                        <AlertCircle size={24} />
                        QR Code
                      </div>
                    )}
                  </div>

                  <div style={{ 
                    background: 'rgba(102, 126, 234, 0.1)', 
                    padding: '10px', 
                    borderRadius: '8px',
                    marginBottom: '15px'
                  }}>
                    <p style={{ margin: '0 0 5px 0', fontSize: '12px', color: 'rgba(255, 255, 255, 0.65)' }}>
                      {t('badges.code')}:
                    </p>
                    <p style={{ 
                      margin: 0, 
                      fontFamily: 'monospace', 
                      fontWeight: 'bold', 
                      color: '#333', 
                      fontSize: '12px',
                      wordBreak: 'break-all'
                    }}>
                      {badge.badge_code}
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => downloadBadgePNG(badge)}
                      style={{
                        flex: '1',
                        padding: '8px 12px',
                        border: 'none',
                        borderRadius: '6px',
                        background: '#10b981',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '5px',
                        transition: 'background-color 0.3s ease'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#059669'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#10b981'; }}
                    >
                      <Download size={14} />
                      PNG
                    </button>
                    <button
                      onClick={() => regenerateBadge(badge.employee_id)}
                      disabled={loading}
                      style={{
                        flex: '1',
                        padding: '8px 12px',
                        border: 'none',
                        borderRadius: '6px',
                        background: loading ? '#6b7280' : '#f59e0b',
                        color: 'white',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '5px',
                        transition: 'background-color 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!loading) e.currentTarget.style.background = '#d97706';
                      }}
                      onMouseLeave={(e) => {
                        if (!loading) e.currentTarget.style.background = '#f59e0b';
                      }}
                    >
                      <span style={{ display: 'inline-flex' }}>
                        <RefreshCw size={14} style={{
                          animation: loading ? 'spin 1s linear infinite' : 'none'
                        }} />
                      </span>
                      {t('badges.regenerate')}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {badges.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255, 255, 255, 0.65)' }}>
                <QrCode size={64} style={{ margin: '0 auto 20px', opacity: 0.3 }} />
                <p style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                  {t('badges.no_badges')}
                </p>
                <p>{t('badges.use_create_tab')}</p>
              </div>
            )}

            {loading && badges.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255, 255, 255, 0.65)' }}>
                <span style={{ display: 'inline-flex' }}>
                  <Loader2 size={32} style={{ 
                    animation: 'spin 1s linear infinite', 
                    margin: '0 auto 20px' 
                  }} />
                </span>
                <p>{t('badges.loading_badges')}</p>
              </div>
            )}
          </div>
        )}

        {/* Création de nouveaux badges */}
        {view === 'create' && (
          <div style={{ 
            background: 'rgba(255,255,255,0.05)', 
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '20px', 
            padding: '24px',
            boxShadow: '0 20px 45px rgba(0,0,0,0.35)'
          }}>
            <h2 style={{ marginBottom: '20px', color: 'rgba(255, 255, 255, 0.9)' }}>
              ➕ {t('badges.create_new_badge')}
            </h2>
            
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ 
                color: '#667eea', 
                marginBottom: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                👥 {t('badges.employees_without_badge')} ({employeesWithoutBadges.length})
                {employeesWithoutBadges.length > 0 && (
                  <button
                    onClick={generateAllBadges}
                    disabled={loading}
                    style={{
                      padding: '5px 10px',
                      border: 'none',
                      borderRadius: '15px',
                      background: loading ? '#6b7280' : '#10b981',
                      color: 'white',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                  >
                    {t('badges.generate_all')}
                  </button>
                )}
              </h3>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                gap: '15px' 
              }}>
                {employeesWithoutBadges.map(employee => (
                  <div key={employee.id} style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '14px',
                    padding: '15px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'transform 0.2s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <div>
                      <h4 style={{ margin: '0 0 5px 0', color: 'rgba(255, 255, 255, 0.9)' }}>
                        {employee.first_name} {employee.last_name}
                      </h4>
                      <p style={{ margin: 0, color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
                        {employee.employee_code} • {employee.department}
                      </p>
                    </div>
                    <button
                      onClick={() => generateBadge(employee.id)}
                      disabled={loading}
                      style={{
                        padding: '8px 16px',
                        border: 'none',
                        borderRadius: '6px',
                        background: (generatingEmployeeId === employee.id || generatingEmployeeId === 'all') ? '#6b7280' : '#667eea',
                        color: 'white',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        transition: 'background-color 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!loading) e.currentTarget.style.background = '#5a67d8';
                      }}
                      onMouseLeave={(e) => {
                        if (!loading) e.currentTarget.style.background = '#667eea';
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        {(generatingEmployeeId === employee.id || generatingEmployeeId === 'all') ? (
                          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                        ) : (
                          <Badge size={16} />
                        )}
                      </span>
                      {t('badges.generate')}
                    </button>
                  </div>
                ))}
              </div>

              {employeesWithoutBadges.length === 0 && employees.length > 0 && (
                <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(255, 255, 255, 0.7)' }}>
                  <Badge size={48} style={{ margin: '0 auto 15px', opacity: 0.3 }} />
                  <p style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                    {t('badges.all_employees_have_badge')}
                  </p>
                  <p>{t('badges.use_existing_tab')}</p>
                </div>
              )}

              {employees.length === 0 && !loading && (
                <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(255, 255, 255, 0.7)' }}>
                  <User size={48} style={{ margin: '0 auto 15px', opacity: 0.3 }} />
                  <p style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>
                    {t('badges.no_employees')}
                  </p>
                  <p>{t('badges.add_employees')}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pointage rapide par code avec mot de passe */}
        {view === 'quick-check' && (
          <div style={{ 
            background: 'rgba(255,255,255,0.05)', 
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '20px', 
            padding: '24px',
            boxShadow: '0 20px 45px rgba(0,0,0,0.35)',
            textAlign: 'center'
          }}>
            <h2 style={{ marginBottom: '20px', color: 'rgba(255, 255, 255, 0.9)' }}>
              ⚡ {t('badges.quick_check_title') || 'Pointage Rapide Sécurisé'}
            </h2>
            
            <div style={{ marginBottom: '30px' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '20px', 
                marginBottom: '20px' 
              }}>
                <Clock size={64} style={{ color: '#10b981' }} />
                <Lock size={64} style={{ color: '#f59e0b' }} />
              </div>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '10px', fontSize: '16px' }}>
                {t('badges.scan_qr_code') || 'Saisissez le code employé et le mot de passe pour un pointage sécurisé'}
              </p>
              <p style={{ color: '#9ca3af', fontSize: '14px' }}>
                {t('badges.auto_determine') || 'Le système détermine automatiquement s\'il s\'agit d\'une arrivée ou d\'un départ'}
              </p>
            </div>

            <div style={{ maxWidth: '500px', margin: '0 auto' }}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '15px', 
                marginBottom: '20px' 
              }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    color: 'rgba(255, 255, 255, 0.85)', 
                    fontWeight: 'bold',
                    textAlign: 'left'
                  }}>
                    👤 {t('badges.enter_code') || 'Code Employé'}
                  </label>
                  <input
                    type="text"
                    value={quickCheckCode}
                    onChange={(e) => setQuickCheckCode(e.target.value.toUpperCase())}
                    placeholder={t('badges.code_placeholder') || 'EMP-001'}
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '15px',
                    border: '2px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      fontSize: '16px',
                      textAlign: 'center',
                      fontFamily: 'monospace',
                      fontWeight: 'bold',
                      boxSizing: 'border-box',
                      backgroundColor: loading ? '#f9f9f9' : 'white',
                      transition: 'border-color 0.3s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.2)'}
                  />
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    color: 'rgba(255, 255, 255, 0.85)', 
                    fontWeight: 'bold',
                    textAlign: 'left'
                  }}>
                    🔒 {t('badges.enter_password') || 'Mot de passe'}
                  </label>
                  <input
                    type="password"
                    value={quickCheckPassword}
                    onChange={(e) => setQuickCheckPassword(e.target.value)}
                    placeholder="••••••••"
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '15px',
                    border: '2px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      fontSize: '16px',
                      textAlign: 'center',
                      boxSizing: 'border-box',
                      backgroundColor: loading ? '#f9f9f9' : 'white',
                      transition: 'border-color 0.3s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.2)'}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && quickCheckCode.trim() && quickCheckPassword.trim() && !loading) {
                        quickAttendance();
                      }
                    }}
                  />
                </div>
              </div>

              <button
                onClick={quickAttendance}
                disabled={loading || !quickCheckCode.trim() || !quickCheckPassword.trim()}
                style={{
                  width: '100%',
                  padding: '15px',
                  border: 'none',
                  borderRadius: '10px',
                  background: (quickCheckCode.trim() && quickCheckPassword.trim() && !loading) ? 
                    'linear-gradient(135deg, #10b981 0%, #059669 100%)' : '#d1d5db',
                  color: 'white',
                  cursor: (quickCheckCode.trim() && quickCheckPassword.trim() && !loading) ? 'pointer' : 'not-allowed',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  transition: 'all 0.3s ease',
                  boxShadow: (quickCheckCode.trim() && quickCheckPassword.trim() && !loading) ? 
                    '0 4px 12px rgba(16, 185, 129, 0.3)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (quickCheckCode.trim() && quickCheckPassword.trim() && !loading) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (quickCheckCode.trim() && quickCheckPassword.trim() && !loading) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                  }
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', position: 'relative', width: 24, height: 24, flexShrink: 0 }}>
                  <span style={{ position: 'absolute', visibility: loading ? 'visible' : 'hidden' }}>
                    <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                  </span>
                  <span style={{ visibility: loading ? 'hidden' : 'visible' }}>
                    <Lock size={20} />
                  </span>
                </span>
                <span>{loading ? (t('badges.checking') || 'Vérification en cours...') : t('badges.check_in')}</span>
              </button>

              <div style={{ 
                marginTop: '20px',
                display: 'flex',
                justifyContent: 'center',
                gap: '20px',
                flexWrap: 'wrap'
              }}>
                <div style={{ 
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '8px',
                  padding: '10px 15px',
                  fontSize: '14px',
                  color: '#166534',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}>
                  <Clock size={16} />
                  {t('badges.quick_auto_arrival')}
                </div>
                <div style={{ 
                  background: '#fef3c7',
                  border: '1px solid #fde68a',
                  borderRadius: '8px',
                  padding: '10px 15px',
                  fontSize: '14px',
                  color: '#92400e',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}>
                  <Lock size={16} />
                  {t('badges.quick_secured_password')}
                </div>
              </div>
            </div>

            {/* Section d'aide et sécurité */}
            <div style={{ 
              background: '#fef3c7', 
              border: '1px solid #f59e0b',
              borderRadius: '15px',
              padding: '25px',
              marginTop: '40px',
              textAlign: 'left'
            }}>
              <h4 style={{ 
                color: '#92400e', 
                marginBottom: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <Lock size={20} />
                🔐 {t('badges.quick_enhanced_security')}
              </h4>
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '15px',
                marginBottom: '20px'
              }}>
                <ul style={{ color: '#92400e', margin: 0, paddingLeft: '20px' }}>
                  <li>✅ {t('badges.quick_auth_required')}</li>
                  <li>🛡️ {t('badges.quick_protection_unauthorized')}</li>
                </ul>
                <ul style={{ color: '#92400e', margin: 0, paddingLeft: '20px' }}>
                  <li>🏢 {t('badges.quick_company_isolation')}</li>
                  <li>📝 {t('badges.quick_traceable_history')}</li>
                </ul>
              </div>
              
              <div style={{ 
                marginTop: '20px',
                padding: '15px',
                background: 'rgba(251, 191, 36, 0.15)',
                borderRadius: '10px',
                border: '1px solid rgba(251, 191, 36, 0.3)'
              }}>
                <p style={{ 
                  margin: '0 0 10px 0', 
                  fontSize: '16px', 
                  color: '#92400e',
                  fontWeight: 'bold'
                }}>
                  💡 {t('badges.quick_how_it_works')}
                </p>
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '10px',
                  fontSize: '14px',
                  color: '#92400e'
                }}>
                  <div>
                    <strong>1️⃣ {t('badges.quick_first_clock')}</strong><br/>
                    → {t('badges.quick_auto_arrival')}
                  </div>
                  <div>
                    <strong>2️⃣ {t('badges.quick_second_clock')}</strong><br/>
                    → {t('badges.quick_auto_departure')}
                  </div>
                  <div>
                    <strong>3️⃣ {t('badges.quick_security_step')}</strong><br/>
                    → {t('badges.quick_password_required')}
                  </div>
                </div>
              </div>

              <div style={{ 
                marginTop: '15px',
                padding: '12px',
                background: 'rgba(239, 68, 68, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(239, 68, 68, 0.3)'
              }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#dc2626' }}>
                  <strong>⚠️ {t('badges.quick_password_important')}</strong>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes fadeIn {
          0% { opacity: 0; transform: translateY(-10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        
        .badges-content {
          animation: slideUp 0.4s ease-out;
        }
        
        button:hover {
          transform: translateY(-1px);
        }
        
        button:active {
          transform: translateY(0);
        }
        
        input:focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
        
        /* Responsive mobile */
        @media (max-width: 768px) {
          .grid-responsive {
            grid-template-columns: 1fr !important;
          }
          
          .flex-responsive {
            flex-direction: column !important;
          }
          
          .text-responsive {
            font-size: 14px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default BadgeManagement;